import React from "react";
import gql from "graphql-tag";
import { useQuery } from "@apollo/client";
import { LoadingPage } from "../UI";
import { Link } from "react-router-dom";
import styled from "@emotion/styled";
import { prefixImageUrl } from "../utils/imageUrl";

const Businesses = styled("div")`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
  ${props => props.theme.bp.below.lg} {
    grid-template-columns: 1fr 1fr;
  }
`;

const CityGroup = styled("section")`
  padding: 0 2rem;
  margin-bottom: 1rem;
  ${props => props.theme.bp.below.lg} {
    padding: 0 1rem;
  }
  h2 {
    text-align: left;
    font-size: 2rem;
    margin-bottom: .5rem;
  }
`;

const Post = styled("article")`
  text-align: left;
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  box-sizing: border-box;
  transition: background-color 0.2s ease-in-out;

  &:hover {
  }

  .business-image-container {
    width: 100%;
    height: 0;
    padding-bottom: 83.75%;
    overflow: hidden;
    position: relative;
    border-radius: 1rem;

    img {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.3s ease;
    }
  }

  &:hover .business-image-container img,
  .business-image-container:hover img {
    transform: scale(1.05);
  }

  .business-content-container {
    padding: 1rem;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    text-align: center;
    ${props => props.theme.bp.below.lg} {
      padding: .5rem 0 0 0;
    }
  }

  span.city-tag {
    position: absolute;
    top: 1rem;
    left: 1rem;
    background: ${(props) => props.theme.colors.primary};
    color: white;
    padding: 8px 15px;
    border-radius: 50px;
    font-size: 11px;
    font-weight: bold;
    z-index: 4;
    text-transform: uppercase;
    letter-spacing: 1.5px;
    ${props => props.theme.bp.below.lg} {
      top: 0.5rem;
      left: 0.5rem;
      font-size: 10px;
      font-weight: normal;
      padding: 6px 10px;
    }

    &.Fastigheter {
      background: ${(props) => props.theme.colors.properties};
    }
    &.Handel,
    &.Köpcenter {
      background: ${(props) => props.theme.colors.retail};
    }
    &.Investeringar {
      background: ${(props) => props.theme.colors.investments};
    }
  }
  h3 {
    margin: 0 0 1rem 0;
    font-size: 1rem;
    font-family: inherit;
    font-weight: 500;
    color: #333;
    a {
      color: inherit;
      text-decoration: none;
    }
  }
  a {
  }

  div.description {
    line-height: 1.5;
    font-size: 0.95rem;
    color: #555;
    margin-bottom: 1.5rem;
    flex-grow: 1;
    text-align: left;
  }
`;

export const BusinessLink = styled("a")`
  text-transform: uppercase;
  border-bottom: 1px solid ${(props) => (props.inline ? "#000" : props.theme.colors.primary)};
  letter-spacing: 2px;
  font-size: 11px;
  color: ${(props) => props.theme.colors.primary};
  text-decoration: none;
  align-self: flex-start;
  margin-top: auto;
  padding-top: 0.5rem;
`;

export const SingleBusiness = ({ post, isRentable = false }) => {  
  let firstImage = null
  if (post?.firstImage?.url) {
    firstImage = prefixImageUrl(post.firstImage.url, "business_box");
  } else if (post?.property?.firstImage?.url) {
    firstImage = prefixImageUrl(post.property.firstImage.url, "business_box");
  } else if (post?.image?.url) {
    firstImage = prefixImageUrl(post.image.url, "business_box");
  } else if (post?.property?.image?.url) {
    firstImage = prefixImageUrl(post.property.image.url, "business_box");
  }

  const targetSlug = isRentable ? post.slug : post.slug;
  const targetLink = isRentable ? `/lediga-objekt/${targetSlug}` : `/fastighet/${targetSlug}`;
  const title = isRentable ? `${post.property.title} - ${post.type}` : post.title;
  const city = isRentable ? (post.city ? post.city : post.property?.city) : post.city;
  const website = isRentable ? null : post.website;
  const category = isRentable ? "Ledigt" : post.category;

  const CardContent = (
    <Post className={category}>
      <div className="business-image-container">
        {city && <span className={`city-tag ${city}`}>{city}</span>}
        {firstImage && <img src={firstImage} alt={title} />}
        {!firstImage && <div style={{ height: '100%' }} />}
      </div>
      <div className="business-content-container">
        <h3>{title}</h3>
      </div>
    </Post>
  );

  if (website) {
    return (
      <a 
        href={website} 
        target="_blank" 
        rel="noopener noreferrer" 
        style={{ textDecoration: 'none', display: 'block', height: '100%' }}
      >
        {CardContent}
      </a>
    );
  } else {
    return (
      <Link 
        to={targetLink} 
        style={{ textDecoration: 'none', display: 'block', height: '100%' }}
      >
        {CardContent}
      </Link>
    );
  }
};

const BusinessListContent = ({
  allProperties,
  allRentable,
  startpageProperties,
  filters,
  first,
  template
}) => {
  let posts = [];
  
  if (allProperties) {
    posts = groupBy(allProperties.edges, (prop) => prop.node.city);
  } else if (allRentable) {
    posts = groupBy(allRentable.edges, (prop) => prop.node.property.city);
  } else {
    posts = groupBy(startpageProperties.edges, (prop) => prop.node.city);
  }


  return (
    <>
      {Array.from(posts).map(([key, value]) => {
        if (filters && filters.length > 0 && filters.indexOf(key) < 0) {
          return null;
        }
        return (
          <CityGroup key={key}>
            {!first && <h2>{key}</h2>}
            <Businesses>
              {value.map((p, i) => {
                return (
                  <SingleBusiness
                    isRentable={typeof allRentable !== "undefined"}
                    post={p.node}
                    key={i}
                  />
                );
              })}
            </Businesses>
          </CityGroup>
        );
      })}
    </>
  );
};

const BusinessList = (props) => {
  const { first, filters, template } = props;
  
  let active_query = query;
  if (first) {
    active_query = start_query;
  }
  if (template === "FREE") {
    active_query = free_query;
  }
  if (template === "SALE") {
    active_query = sale_query;
  }

  const { loading, error, data } = useQuery(active_query, {
    variables: { first },
    skip: !active_query
  });

  if (loading) return <LoadingPage />;
  if (error) {
    console.error("Error fetching business list:", error);
    return <p>Error loading list.</p>;
  }

  return (
    <BusinessListContent
      filters={filters}
      {...data}
      first={first}
      template={template}
    />
  );
};

const query = gql`
  query Properties($first: Int) {
    allProperties(first: $first) {
      edges {
        node {
          category
          title
          slug
          website
          city
          forSale
          firstImage {
            url
          }
          image(version: "business_box") {
            url
          }
        }
      }
    }
  }
`;

const free_query = gql`
  query freeQuery {
    allRentable {
      edges {
        node {
          slug
          content
          contact {
            name
            email
            phone
          }
          firstImage {
            url
          }
          type
          space
          property {
            title
            city
            address
            zipCode
            firstImage {
              url
            }
            image(version: "business_box") {
              url
              width
              height
            }
            year
            elevator
          }
        }
      }
    }
  }
`;

const sale_query = gql`
  query saleQuery {
    allProperties(forSale: true) {
      edges {
        node {
          category
          title
          slug
          website
          city
          firstImage {
            url
          }
          image(version: "business_box") {
            url
          }
        }
      }
    }
  }
`;

const start_query = gql`
  query startPageProps($first: Int) {
    startpageProperties(first: $first) {
      edges {
        node {
          category
          title
          slug
          website
          city
          image {
            url
          }
        }
      }
    }
  }
`;

export function groupBy(list, keyGetter) {
  const map = new Map();
  list.forEach((item) => {
    const key = keyGetter(item);
    const collection = map.get(key);
    if (!collection) {
      map.set(key, [item]);
    } else {
      collection.push(item);
    }
  });
  return map;
}

export default BusinessList;
